/**
 * trainerIdentity.ts
 *
 * Single source of truth for "what name do we show this user?".
 *
 * Rule: the user-facing identity in the app is ALWAYS the PoGo trainer name
 * (typed by the trainer at sign-up, lib/trainerSetup; read off a profile
 * screenshot on builds before 2026-10-02). The Google / Apple IdP displayName
 * must never leak into user-facing UI: it can be the user's real-world legal
 * name, which violates our privacy posture.
 *
 * Resolution order for the rendered name:
 *   1. account.trainerName  -> the PoGo trainer name
 *   2. account.username     -> the slugified version of trainerName
 *   3. fallback             -> "Trainer" (or caller-supplied)
 *
 * NEVER fall back to account.displayName for UI text. account.displayName
 * is kept on the record only as an internal hint from the IdP at signup
 * time and as a last-resort signal if everything else is missing.
 */

export interface IdentitySource {
  trainerName?: string | null;
  username?: string | null;
  // displayName is intentionally NOT read by these helpers.
}

/**
 * The display label for a trainer in any user-facing surface
 * (greetings, profile chip, friends list rows, trade rows, etc.).
 */
export function trainerDisplayName(
  account: IdentitySource | null | undefined,
  fallback: string = "Trainer",
): string {
  const t = account?.trainerName?.trim();
  if (t) return t;
  const u = account?.username?.trim();
  if (u) return u;
  return fallback;
}

/**
 * The @-handle form. Prefers username (already a slug); falls back to
 * a slugified trainerName if username is somehow missing.
 */
export function trainerHandle(
  account: IdentitySource | null | undefined,
): string | null {
  const u = account?.username?.trim();
  if (u) return `@${u}`;
  const t = account?.trainerName?.trim();
  if (t) return `@${t.replace(/\s+/g, "").toLowerCase()}`;
  return null;
}

/**
 * The first visible initial of a name, for monogram avatars.
 *
 * Uses the first Unicode LETTER or NUMBER rather than `charAt(0)`: a raw
 * `charAt(0)` returns a lone high-surrogate for a name that begins with an
 * emoji or any non-BMP glyph (e.g. a Google displayName like "🎮 Ash"), which
 * renders as an invisible tofu — the "missing initial in the hexagon" bug. If
 * the name carries no letter/digit at all we return the caller's fallback.
 */
// Built via `new RegExp` (not a `/…/u` literal) so tsc's default-target check
// doesn't reject the Unicode flag; the flag itself is fine at runtime.
const FIRST_LETTER_OR_DIGIT = new RegExp("[\\p{L}\\p{N}]", "u");
export function firstInitial(name: string, fallback: string = "T"): string {
  const m = name.match(FIRST_LETTER_OR_DIGIT);
  return (m ? m[0] : fallback).toUpperCase();
}

/**
 * Single uppercase letter used by Monogram avatars.
 */
export function trainerInitial(
  account: IdentitySource | null | undefined,
  fallback: string = "T",
): string {
  return firstInitial(trainerDisplayName(account, fallback), fallback);
}
